<template>
  <div
    class="play-col"
    :class="colClasses"
  >
    <slot />
  </div>
</template>

<script>
/**
 * PlayCol — a column inside <PlayRow> for the Play system.
 *
 * Replaces bootstrap-vue's <b-col>. 12-column model with Bootstrap-parity
 * breakpoints (sm/md/lg/xl). Each size prop takes 1–12, 'auto', or '' (empty =
 * equal-width flex). `alignSelf` overrides the row's vertical alignment for
 * this column; `order` sets the flex order.
 *
 * VUE3-MIGRATION: nothing Vue 2-specific — already Vue 3-safe.
 */
const BREAKPOINTS = ['sm', 'md', 'lg', 'xl']

export default {
  name: 'PlayCol',
  props: {
    /** Base (xs) span: 1–12 | 'auto' | '' (equal-width) */
    cols: { type: [String, Number], default: '' },
    sm: { type: [String, Number], default: '' },
    md: { type: [String, Number], default: '' },
    lg: { type: [String, Number], default: '' },
    xl: { type: [String, Number], default: '' },
    /** flex order */
    order: { type: [String, Number], default: '' },
    /** Per-column vertical alignment → align-self */
    alignSelf: {
      type: String,
      default: '',
      validator: v => ['', 'start', 'center', 'end', 'stretch', 'baseline'].includes(v),
    },
  },
  computed: {
    colClasses() {
      const classes = []
      // base span
      if (this.cols !== '' && this.cols !== null) classes.push(`play-col--${this.cols}`)
      // responsive spans
      BREAKPOINTS.forEach(bp => {
        const val = this[bp]
        if (val !== '' && val !== null) classes.push(`play-col--${bp}-${val}`)
      })
      // no explicit base → equal-width flex
      if (!classes.length) classes.push('play-col--equal')
      if (this.order !== '' && this.order !== null) classes.push(`play-col--order-${this.order}`)
      if (this.alignSelf) classes.push(`play-col--self-${this.alignSelf}`)
      return classes
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:math';

.play-col {
  position: relative;
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
}

/* equal-width (no span given) */
.play-col--equal { flex: 1 0 0%; }
.play-col--auto  { flex: 0 0 auto; width: auto; }

/* ── Span generators ── */
@mixin col-span($n) {
  flex: 0 0 percentage(math.div($n, 12));
  max-width: percentage(math.div($n, 12));
}

@for $i from 1 through 12 {
  .play-col--#{$i} { @include col-span($i); }
}

/* order */
@for $i from 0 through 12 {
  .play-col--order-#{$i} { order: $i; }
}

/* align-self */
.play-col--self-start    { align-self: flex-start; }
.play-col--self-center   { align-self: center; }
.play-col--self-end      { align-self: flex-end; }
.play-col--self-stretch  { align-self: stretch; }
.play-col--self-baseline { align-self: baseline; }

/* ── Responsive spans (Bootstrap-parity breakpoints) ── */
@media (min-width: 576px) {
  .play-col--sm-auto { flex: 0 0 auto; width: auto; max-width: none; }
  @for $i from 1 through 12 {
    .play-col--sm-#{$i} { @include col-span($i); }
  }
}

@media (min-width: 768px) {
  .play-col--md-auto { flex: 0 0 auto; width: auto; max-width: none; }
  @for $i from 1 through 12 {
    .play-col--md-#{$i} { @include col-span($i); }
  }
}

@media (min-width: 992px) {
  .play-col--lg-auto { flex: 0 0 auto; width: auto; max-width: none; }
  @for $i from 1 through 12 {
    .play-col--lg-#{$i} { @include col-span($i); }
  }
}

@media (min-width: 1200px) {
  .play-col--xl-auto { flex: 0 0 auto; width: auto; max-width: none; }
  @for $i from 1 through 12 {
    .play-col--xl-#{$i} { @include col-span($i); }
  }
}
</style>
